import { useEffect, useMemo } from 'react';

import { LatestIndexRunDTO } from '../Types/indexes';
import { latestIndexRunPathUrl } from '../util/routesUrls';
import { useFetchData } from './useFetchData';

/**
 * Custom hook to retrieve the vector store indexing status and latest index run data for a specific index.
 *
 * @param indexId - The ID of the index to fetch status for. If undefined, no data will be fetched.
 *
 * @returns An object containing:
 * - `isIndexing`: A boolean indicating whether the index is currently being indexed.
 * - `latestIndexRunValues`: The latest index run data for the specified index, or `undefined` if not available.
 * - `lastIndexDate`: The date of the last file index run, or `null` if not available.
 * - `refetchLatestIndexRunValues`: Function to manually refetch the latest index run data.
 *
 * Indexing is considered in progress if the status is not `COMPLETE` and does not start with `'Error'`.
 * When indexing is in progress, the hook automatically polls for updates every 20 seconds.
 */
const POLLING_INTERVAL = 20000;

export const useIndexRun = (indexId: string | undefined) => {
  const {
    data: latestIndexRunValues,
    refetch: refetchLatestIndexRunValues,
    startPolling: startPollingIndexRun,
    stopPolling: stopPollingIndexRun,
  } = useFetchData<LatestIndexRunDTO | undefined>({
    urlPath: indexId ? latestIndexRunPathUrl(indexId) : '',
    skip: !indexId,
  });

  const isIndexing = useMemo(() => {
    const status = latestIndexRunValues?.status;
    if (!status) return false;
    return status !== 'COMPLETE' && !status?.startsWith('Error');
  }, [latestIndexRunValues]);

  const lastIndexDate = useMemo(
    () =>
      latestIndexRunValues?.lastFileIndexRunTime
        ? new Date(latestIndexRunValues.lastFileIndexRunTime)
        : null,
    [latestIndexRunValues]
  );

  useEffect(() => {
    if (isIndexing) {
      startPollingIndexRun(POLLING_INTERVAL);
    }

    return () => {
      stopPollingIndexRun();
    };
  }, [isIndexing, startPollingIndexRun, stopPollingIndexRun]);

  return {
    isIndexing,
    latestIndexRunValues: indexId ? latestIndexRunValues : undefined,
    lastIndexDate: indexId ? lastIndexDate : null,
    refetchLatestIndexRunValues,
  };
};
